import React, { useEffect, useRef, useState } from "react";
import { View, Text, Animated, Easing, Dimensions } from "react-native";
import { PIELABELS, YOU } from "../../Utils/static-strings";

const parseDisplayValue = (value) => {
  const raw = value == null ? "" : String(value);
  const trimmed = raw.trim();
  if (!trimmed) {
    return { number: 0, prefix: "", suffix: "" };
  }
  const match = trimmed.match(/-?[\d.,]+/);
  if (!match) {
    return { number: 0, prefix: trimmed, suffix: "" };
  }
  const numberPart = match[0];
  const numberValue = Number(numberPart.replace(/,/g, ""));
  const prefix = trimmed.slice(0, match.index);
  const suffix = trimmed.slice(match.index + numberPart.length);
  return {
    number: Number.isFinite(numberValue) ? numberValue : 0,
    prefix,
    suffix,
  };
};

const LabelLeft = ({
  gain,
  setDisable,
  setTriangle,
  circularPieForMandG,
  showAnimation = true,
  showPercentage,
  isGain,
  isBlueOverDark,
  hideBracket,
  useBiggerFont,
}) => {
  const winAnimatedValue = useRef(new Animated.Value(0)).current;
  const scaleValue = useState(new Animated.Value(1))[0];
  const translateValue = useState(new Animated.Value(0))[0];
  const inputRef = useRef(null);
  const [gainValue, setGainValue] = useState(0);
  const [gainPrefix, setGainPrefix] = useState("");
  const [gainSuffix, setGainSuffix] = useState("");

  const runAnimation = (toValue) => {
    setDisable(true);
    Animated.sequence([
      Animated.parallel([
        Animated.timing(scaleValue, { toValue: 1.4, duration: 600 }),
        Animated.timing(translateValue, {
          toValue: -10,
          duration: 600,
          easing: Easing.ease,
        }),
      ]),
      Animated.timing(winAnimatedValue, { toValue, duration: 900 }),
      Animated.parallel([
        Animated.timing(scaleValue, { toValue: 1, duration: 600 }),
        Animated.timing(translateValue, { toValue: 0, duration: 600 }),
      ]),
    ]).start(() => setDisable(false));
  };

  useEffect(() => {
    const { number, prefix, suffix } = parseDisplayValue(gain);
    setGainPrefix(prefix);
    setGainSuffix(suffix);

    runAnimation(number);
    if (showAnimation) {
      const listenerId = winAnimatedValue.addListener((v) => {
        setGainValue(Math.round(v.value));
      });
      return () => winAnimatedValue.removeListener(listenerId);
    } else {
      setGainValue(Math.round(number));
    }
  }, [gain, showAnimation, winAnimatedValue]);

  const screenHeight = Dimensions.get("window").height;
  const getResponsiveFontSize = (size) => {
    const baseScreenHeight = 680;
    let scaleRatio = screenHeight / baseScreenHeight;
    if (scaleRatio > 1.69) scaleRatio = 1.69;
    let fontSize = size * scaleRatio;
    if (fontSize > 32) {
      fontSize = 32;
    }
    return fontSize;
  };

  return (
    <View
      style={[
        circularPieForMandG
          ? {
              position: "absolute",
              alignSelf: "center",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 55,
              elevation: 5,
              transform: [
                { translateX: setTriangle ? "90%" : "90%" },
                { scaleX: -1 },
              ],
              bottom: isBlueOverDark ? "positionLeftBottom" : "initial",
            }
          : {
              marginRight: 0,
              position: "absolute",
              alignSelf: "center",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 55,
              elevation: 5,
              transform: [
                { translateX: setTriangle ? "60%" : "60%" },
                { scaleX: -1 },
              ],
              bottom: isBlueOverDark ? "positionLeftBottom" : "initial",
            },
      ]}
    >
      <Animated.View
        style={{
          transform: [{ translateY: !showAnimation ? 0 : translateValue }],
        }}
      >
        {circularPieForMandG ? (
          <Text
            style={{
              color: "var(--text-color-la-left)",
              fontSize: getResponsiveFontSize(22),
              fontFamily: "Infra",
              fontWeight: "var(--font-weight-bold)",
              letterSpacing: 0.5,
              lineHeight: 12,
            }}
          >
            Gain
          </Text>
        ) : (
          <Text
            style={{
              color: "var(--text-color-la-left)",
              fontSize: getResponsiveFontSize(22),
              fontFamily: "var(--font-family-default)",
              fontWeight: "var(--font-weight-medium)",
              letterSpacing: 0.5,
            }}
          >
            {YOU}{" "}
            <Text
              style={{ color: "var(--text-color-la-left)", fontWeight: 900 }}
            >
              {isGain ? "Gain" : PIELABELS.win}
            </Text>
          </Text>
        )}
      </Animated.View>

      <Animated.View
        style={{
          transform: [{ scale: !showAnimation ? 1 : scaleValue }],
          top: 5,
          alignItems: "center",
        }}
      >
        <View style={{ alignItems: "center" }}>
          {circularPieForMandG ? (
            <Text
              ref={inputRef}
              style={{
                color: "var(--text-color-la-left)",
                fontSize: getResponsiveFontSize(21),
                textAlign: "center",
                fontFamily: "Infra",
                fontWeight: "var(--font-weight-medium)",
                letterSpacing: 0.5,
              }}
            >
              {gainPrefix}
              {gainValue}
              {gainSuffix}
            </Text>
          ) : null}
          {showPercentage && (
            <Text
              style={{
                color: "var(--text-color-la-left)",
                fontSize: getResponsiveFontSize(35),
                textAlign: "center",
                fontFamily: circularPieForMandG ? "Infra" : "Josefin Sans",
                fontWeight: "var(--font-weight-medium)",
                letterSpacing: 0.5,
              }}
            >
              {hideBracket ? "" : "("}
              {gainPrefix}
              {gainValue}
              {gainSuffix}
              {hideBracket ? "" : ")"}
            </Text>
          )}
        </View>
      </Animated.View>
    </View>
  );
};

export default LabelLeft;
